import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFile, saveBlob } from '../api';
import { ErrorBox, Field, Loading, Success, fmtDate } from './ui';

export interface RetailPriceRow {
  packSize: number | null;
  price: string;
  currency: string;
  source: 'rule' | 'manual';
  basisAmount: string | null;
  basisSource: 'manifest_price' | 'costco_page' | null;
  basisUnits: number | null;
  rulePercent: string | null;
  version: number;
  setAt: string;
  setByName: string;
}
export interface RetailInfo {
  itemNumber: string;
  category: string | null;
  costcoRetail: { amount: string; currency: string; source: 'manifest_price' | 'costco_page' } | null;
  suggestion: { price: string; currency: string; percent: number } | null;
  prices: RetailPriceRow[];
  history: { packSize: number | null; price: string; source: string; reason: string; changedAt: string; changedByName: string }[];
}

const BASIS: Record<string, string> = { manifest_price: 'manifest / master list', costco_page: 'Costco website' };

export const useRetailInfo = (productId: string) =>
  useQuery({ queryKey: ['retail', productId], queryFn: () => api<RetailInfo>(`/products/${productId}/retail`) });

/** Retail (store) price for an item number: the rule's suggestion, the price in use, overrides, labels and history. */
export function RetailPriceCard({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const q = useRetailInfo(productId);
  const [typed, setTyped] = useState('');
  const [copies, setCopies] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const info = q.data!;
  const whole = info.prices.find((p) => p.packSize === null) ?? null;

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      await fn();
      setMsg(done);
      await qc.invalidateQueries({ queryKey: ['retail', productId] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const setPrice = (price: string | null) =>
    run(() => api(`/products/${productId}/retail`, { method: 'PUT', body: { price, version: whole?.version ?? null } }), price ? 'Price saved.' : 'Price set from the rule.');
  const labels = () =>
    run(async () => saveBlob(await apiFile('/retail/labels.pdf', { items: [{ productId, copies: Number(copies) || 1 }] }), `price-labels-${info.itemNumber}.pdf`), 'Labels ready.');

  return (
    <section className="card">
      <h2>Retail price</h2>
      {whole ? (
        <div className="retail-price">
          <span className="retail-price-amount">${whole.price}</span>
          <span className="muted small">
            {whole.source === 'rule'
              ? `${Number(whole.rulePercent)}% of Costco $${whole.basisAmount} (${BASIS[whole.basisSource ?? ''] ?? ''})`
              : 'typed by staff'}{' '}
            · set {fmtDate(whole.setAt)} by {whole.setByName}
          </span>
        </div>
      ) : (
        <p className="muted">No retail price yet.</p>
      )}
      {info.suggestion ? (
        <p className="small">
          Rule: {info.suggestion.percent}% of Costco ${info.costcoRetail?.amount} ({BASIS[info.costcoRetail?.source ?? '']}) = <strong>${info.suggestion.price}</strong>
          {whole && whole.price !== info.suggestion.price && <span className="badge badge-warn">differs from the price in use</span>}{' '}
          {(!whole || whole.price !== info.suggestion.price) && (
            <button className="btn btn-small" disabled={busy} onClick={() => setPrice(null)}>
              {whole ? 'Use this price' : 'Set this price'}
            </button>
          )}
        </p>
      ) : (
        <p className="small muted">No Costco retail price is known for this item, so type a price.</p>
      )}
      <div className="row gap">
        <Field label={whole ? 'Change to' : 'Type a price'}>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} inputMode="decimal" placeholder="e.g. 24.99" />
        </Field>
        <button className="btn" disabled={busy || !typed.trim()} onClick={() => setPrice(typed).then(() => setTyped(''))}>
          Save price
        </button>
      </div>
      {whole && (
        <div className="row gap">
          <Field label="Price labels (2 × 1 in)">
            <input value={copies} onChange={(e) => setCopies(e.target.value)} inputMode="numeric" style={{ width: '5rem' }} />
          </Field>
          <button className="btn" disabled={busy} onClick={labels}>
            Print labels
          </button>
        </div>
      )}
      {msg && <Success>{msg}</Success>}
      <ErrorBox error={error} />
      {info.history.length > 0 && (
        <details>
          <summary>Price history ({info.history.length})</summary>
          <ul className="history">
            {info.history.map((h, i) => (
              <li key={i}>
                {fmtDate(h.changedAt)}: <strong>${h.price}</strong>
                {h.packSize ? ` (${h.packSize}-pack)` : ''}, {h.reason}, by {h.changedByName}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
